import type { SchemaFieldDefinition, SchemaFieldType } from "@erp/shared-types";
import type { SocketType } from "../types";

export interface ExtractionSocketDefinition {
  id: string;
  name: string;
  type: SocketType;
  required: boolean;
  description?: string;
}

const PRIMITIVE_TYPE_MAP: Partial<Record<SchemaFieldType, SocketType>> = {
  string: "string",
  text: "string",
  url: "string",
  geospatial: "string",
  unit: "string",
  enum: "string",
  integer: "number",
  float: "number",
  currency: "number",
  range: "number",
  boolean: "boolean",
  date: "date",
  time: "string",
  datetime: "date",
  file: "json",
  image: "json",
  json: "json",
  object: "json",
  array: "array<json>",
};

function arrayWrap(inner: SocketType): SocketType {
  switch (inner) {
    case "string":
      return "array<string>";
    case "number":
      return "array<number>";
    case "boolean":
      return "array<boolean>";
    case "uuid":
      return "array<uuid>";
    default:
      return "array<json>" as SocketType;
  }
}

export function socketTypeForField(field: SchemaFieldDefinition): SocketType {
  if (field.fieldType === "array") {
    const children = field.children ?? [];
    if (children.length === 1) {
      return arrayWrap(socketTypeForField(children[0]));
    }
    return "array<json>";
  }

  const inner = PRIMITIVE_TYPE_MAP[field.fieldType] ?? ("json" as SocketType);
  return field.isMultiple ? arrayWrap(inner) : inner;
}

export interface ExtractionSchemaToSocketsOptions {
  mode?: "per_field" | "single_object";
  wrapInArray?: boolean;
  idPrefix?: string;
  singleSocketId?: string;
  singleSocketName?: string;
  singleSocketDescription?: string;
  /** When true, socket `required` mirrors `field.isRequired` (HTTP trigger). */
  respectFieldRequired?: boolean;
}

export function extractionSchemaToOutputSockets(
  fields: SchemaFieldDefinition[],
  opts: ExtractionSchemaToSocketsOptions = {},
): ExtractionSocketDefinition[] {
  const {
    mode = "per_field",
    wrapInArray = false,
    idPrefix = "field:",
    singleSocketId = "extractedFields",
    singleSocketName = "Extracted Fields",
    singleSocketDescription = "Typed object(s) carrying all fields declared by the pinned schema version",
    respectFieldRequired = false,
  } = opts;

  if (mode === "single_object") {
    return [
      {
        id: singleSocketId,
        name: singleSocketName,
        type: (wrapInArray ? "array<json>" : "json") as SocketType,
        required: false,
        description: singleSocketDescription,
      },
    ];
  }

  return fields.map((field) => {
    const baseType = socketTypeForField(field);
    const type = wrapInArray ? arrayWrap(baseType) : baseType;
    return {
      id: `${idPrefix}${field.fieldName}`,
      name: field.label || field.fieldName,
      type,
      required: respectFieldRequired ? field.isRequired : false,
      description: field.description ?? undefined,
    };
  });
}
